import { useEffect, useState, type CSSProperties } from 'react';
import type { GradeShareData, GradingProvider, ProviderGradeResult } from '../lib/types';
import { GRADING_PROVIDERS } from '../lib/types';
import {
  formatCenteringPair,
  formatDate,
  formatGrade,
  percentArticle,
  toPercent,
} from '../lib/format';
import { Avatar } from './Avatar';
import { EvoLogoFullSvg, ProviderLogoSvg } from './icons';
import { Slab } from './slab/Slab';
import { deriveSlabPaletteFromImage, slabPalette, type SlabPalette } from './slab/palette';
import styles from './preview.module.css';

const HIGH_CONFIDENCE = 0.7;

export function GradeSharePreviewImage({ data, onPaletteReady }: {
  data: GradeShareData;
  onPaletteReady?: () => void;
}) {
  const provider = firstProvider(data) ?? 'PSA';
  const result = data.providerGrades[provider];
  const [palette, setPalette] = useState<SlabPalette>(() => slabPalette(data.card.paletteColor));

  useEffect(() => {
    let cancelled = false;
    deriveSlabPaletteFromImage(data.card.frontImageUrl).then((sampled) => {
      if (cancelled) return;
      if (sampled) setPalette(sampled);
      onPaletteReady?.();
    });
    return () => {
      cancelled = true;
    };
  }, [data.card.frontImageUrl, onPaletteReady]);

  if (!result) return null;

  const top = topGrade(result);
  const topPercent = toPercent(top.p);
  const damageFront = data.imperfections.front.count;
  const damageBack = data.imperfections.back.count;

  const previewStyle = {
    '--preview-glow': palette.pageGlow,
    '--preview-mid': palette.pageMid,
    '--preview-tail': palette.bodyBottom,
  } as CSSProperties;

  return (
    <main className={styles.preview} style={previewStyle} data-grade-share-preview>
      <header className={styles.header}>
        <div className={styles.headerCopy}>
          <h1>
            AI grade report from <EvoLogoFullSvg height={41} />
          </h1>
          <div className={styles.gradedBy}>
            Graded on {formatDate(data.gradedAt)} by
            <Avatar
              pokemonId={data.user.pokemonId}
              profileColor={data.user.profileColor}
              username={data.user.username}
              size={25}
            />
            <span>{data.user.username}</span>
          </div>
        </div>
        <div className={styles.cta}>
          <div className={styles.ctaButton}>Get a free AI grade</div>
          <div>Use code {data.user.referralCode || 'EVO'} on signup</div>
        </div>
      </header>

      <section className={styles.body}>
        <div className={styles.slabColumn}>
          <Slab
            className={styles.previewSlab}
            showActions={false}
            flipEnabled={false}
            palette={palette}
            frontImageUrl={data.card.frontImageUrl}
            backImageUrl={data.card.backImageUrl}
            frontImageRectificationApplied={data.card.frontImageRectificationApplied}
            backImageRectificationApplied={data.card.backImageRectificationApplied}
            cardName={data.card.name}
            cardNumber={data.card.number}
            officialCount={data.card.officialCount}
            setName={data.card.setName}
            language={data.card.language}
            grade={formatGrade(result.grade, provider)}
            provider={provider}
            confidencePercent={topPercent}
            paletteColor={data.card.paletteColor}
            analysis={data.analysis}
            centering={data.centering}
            useInlineLogos
          />
        </div>

        <div className={styles.reportColumn}>
          <GradePrediction provider={provider} result={result} />

          <section className={styles.metricSection}>
            <h3>
              <span className={styles.centeringIcon}>#</span>
              Centering
            </h3>
            <div className={styles.centeringTable}>
              <MetricRow
                label="Front T/B"
                value={formatCenteringPair(data.centering.front.top, data.centering.front.bottom)}
              />
              <MetricRow
                label="Front L/R"
                value={formatCenteringPair(data.centering.front.left, data.centering.front.right)}
              />
              <MetricRow
                label="Back T/B"
                value={formatCenteringPair(data.centering.back.top, data.centering.back.bottom)}
              />
              <MetricRow
                label="Back L/R"
                value={formatCenteringPair(data.centering.back.left, data.centering.back.right)}
              />
            </div>
          </section>

          <section className={styles.metricSection}>
            <h3>
              <span className={styles.damageIcon}>%%</span>
              Damage
            </h3>
            <div className={styles.damageGrid}>
              <DamageBox label="Front" count={damageFront} />
              <DamageBox label="Back" count={damageBack} />
            </div>
            <p className={styles.microcopy}>
              Print lines, dents, and other forms of damage not analyzed yet
            </p>
          </section>

          <p className={styles.disclaimer}>
            Evo uses custom AI models to predict grades, trained off real slab data.
            AI can make mistakes, so prediction results may vary.
          </p>
        </div>
      </section>
    </main>
  );
}

function GradePrediction({
  provider,
  result,
}: {
  provider: GradingProvider;
  result: ProviderGradeResult;
}) {
  const top = topGrade(result);
  const topPercent = toPercent(top.p);
  const confident = top.p >= HIGH_CONFIDENCE;
  const segments = topSegments(result);
  const nonTopCount = segments.filter((segment) => segment.grade !== top.grade).length;
  const nonTopWidth = nonTopCount > 1 ? 86 : 142;

  return (
    <section className={styles.prediction}>
      <h2>
        This card has {percentArticle(topPercent)}{' '}
        <span className={confident ? styles.green : styles.orange}>{topPercent}%</span>{' '}
        chance of receiving a{' '}
        <span className={styles.providerGrade}>
          <ProviderLogoSvg provider={provider} height={31} />
          {formatGrade(top.grade, provider)}.
        </span>
      </h2>
      <div className={styles.distribution}>
        {segments.map((segment) => {
          const isTop = segment.grade === top.grade;
          return (
            <div
              key={segment.grade}
              className={styles.segmentColumn}
              style={
                isTop
                  ? { flex: '1 1 auto' }
                  : { flex: `0 0 ${nonTopWidth}px` }
              }
            >
              <div
                className={`${styles.segment} ${
                  isTop ? (confident ? styles.segmentTop : styles.segmentWarn) : ''
                }`}
              >
                {toPercent(segment.p)}%
              </div>
              <div className={styles.segmentLabel}>{formatGrade(segment.grade, provider)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metricRow}>
      <span>{label}</span>
      <i />
      <strong>{value}</strong>
    </div>
  );
}

function DamageBox({ label, count }: { label: string; count: number }) {
  return (
    <div className={styles.damageBox}>
      <span>{label}</span>
      <strong className={count > 0 ? styles.damageFound : styles.green}>
        {count > 0 ? `${count} found` : 'None found'}
      </strong>
    </div>
  );
}

function firstProvider(data: GradeShareData): GradingProvider | null {
  return GRADING_PROVIDERS.find((provider) => data.providerGrades[provider]) ?? null;
}

function topGrade(result: ProviderGradeResult) {
  const entries = Object.entries(result.confidence_map).map(([grade, p]) => ({
    grade: Number(grade),
    p,
  }));
  if (!entries.length) return { grade: result.grade, p: 1 };
  return entries.reduce((best, entry) => (entry.p > best.p ? entry : best), entries[0]);
}

function topSegments(result: ProviderGradeResult) {
  const segments = Object.entries(result.confidence_map)
    .map(([grade, p]) => ({ grade: Number(grade), p }))
    .filter((segment) => toPercent(segment.p) > 0)
    .sort((a, b) => b.p - a.p || b.grade - a.grade)
    .slice(0, 3)
    .sort((a, b) => a.grade - b.grade);
  return segments.length ? segments : [{ grade: result.grade, p: 1 }];
}
